import { useState } from 'react'
import { useDispatch } from 'react-redux'
import styled, { css, keyframes, useTheme } from 'styled-components'
import { match } from 'ts-pattern'
import { ProductDetailResponseDto } from '@/apis/rest/generated/types'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { Typo, TypoVariant } from '@/components/common/typography'
import ModalRestockProductOptionAdded from '@/components/domains/modals/ModalRestockProductOptionAdded'
import { PRODUCT_STATE } from '@/components/domains/product-detail/product-detail.type'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { ProductLocation } from '@/constants/product-location.const'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'
import { useRestockProductOption } from '@/containers/hooks'
import { useOpenProductOptionChoiceBottomSheet } from '@/containers/hooks/cart/useOpenProductOptionChoiceBottomSheet'
import { useDebounceCallback } from '@/containers/hooks/common/useDebounceCallback.hook'
import { useMutationProductLike } from '@/containers/hooks/like/useMutationProductLike'
import { doSetToggleModalLogin } from '@/stores/reduxUI'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { encodeId } from '@/utils/utilApi'

interface ProductDetailFixedBottomProps {
  isUserLoggedIn: boolean
  productDetailData: ProductDetailResponseDto
}

export const ProductDetailFixedBottom = ({ isUserLoggedIn, productDetailData }: ProductDetailFixedBottomProps) => {
  const { color } = useTheme()
  const { toggleProductLike } = useMutationProductLike()
  const dispatch = useDispatch()
  const {
    id: productId,
    promotion,
    defaultOption,
    isLiked,
    isRealSoldOut,
    isNotAvailable,
    requestRestockList,
    name,
    price,
    discountRate,
    salePrice,
    salePriceAfterCoupon,
    review: { count: reviewCount, score: reviewScore },
  } = productDetailData
  const promotionId = promotion?.id
  const promotionOptionId = defaultOption?.id
  const [isLikedState, setIsLikedState] = useState(isLiked)
  const [isRequestRestockState, setIsRequestRestockState] = useState(requestRestockList.length > 0)

  // 상품상태 우선순위 isNotAvailable > isRealSoldOut > isAvailable
  const productState = isNotAvailable
    ? PRODUCT_STATE.isNotAvailable
    : isRealSoldOut
    ? PRODUCT_STATE.isRealSoldOut
    : PRODUCT_STATE.isAvailable

  const { requestRestockProductOption, restockProductOptionAddedProps } = useRestockProductOption()
  const { openProductOptionChoiceBottomSheet } = useOpenProductOptionChoiceBottomSheet()
  const { trackClickProductDetailStartPurchaseButtonEvent, trackClickProductDetailSingleOptionRestockButton } =
    useProductDetailEventTracker()

  const handleToggleLikeButton = async () => {
    if (!isUserLoggedIn) {
      dispatch(doSetToggleModalLogin(true))
      return
    }

    const isPrevLikedState = isLikedState
    setIsLikedState((prev) => !prev)
    try {
      await toggleProductLike({
        productId,
        promotionId,
        isLike: !isLikedState,
        productName: name,
        price,
        discountRate: discountRate || 0,
        discountPrice: salePriceAfterCoupon || salePrice,
        reviewCount,
        reviewScore,
      })
    } catch {
      setIsLikedState(isPrevLikedState)
    }
  }

  const { debouncedFunction } = useDebounceCallback({
    callback: handleToggleLikeButton,
    delay: 300,
  })

  const handleClickPurchaseButton = () => {
    trackClickProductDetailStartPurchaseButtonEvent({
      brandId: productDetailData.brand.id || 0,
      brandName: productDetailData.brand.name,
      productId: productDetailData.id,
      productName: productDetailData.name,
      isRealSoldOut: productDetailData.isRealSoldOut,
      price: productDetailData.price,
      discountRate: productDetailData.discountRate || 0,
      salePrice: productDetailData.salePrice,
      reviewScore: productDetailData.review.score,
      reviewCount: productDetailData.review.count,
      isTimeSale: productDetailData.isTimeSale,
      categories: productDetailData.categories,
      supplyType: productDetailData.supplyType,
    })

    openProductOptionChoiceBottomSheet({
      product: transformProductTypeDefinedByFrontend(productDetailData),
      trackerData: {
        addLocation: ProductLocation.ProductDetail,
      },
    })
  }

  const handleClickRestockAlarmButton = async ({ isRestockAlarm }: { isRestockAlarm: boolean }) => {
    const encodedProductOptionId = encodeId('ProductOptionType', promotionOptionId)
    const tracker = () =>
      trackClickProductDetailSingleOptionRestockButton({
        isRestockAlarm,
        product: productDetailData,
      })
    const result = await requestRestockProductOption(isRestockAlarm, encodedProductOptionId, tracker)
    if (result) {
      setIsRequestRestockState(true)
    } else {
      setIsRequestRestockState(false)
    }
  }

  return (
    <ProductDetailFixedBottomWrapper className="product-detail-fixed-bottom" isAnimating={isLikedState}>
      <button
        type="button"
        className="product-detail-fixed-bottom__like"
        onClick={debouncedFunction}
        aria-label="찜하기"
      >
        {isLikedState ? (
          <FlatIcon type={IconNameEnum.IcHeartFilled} size="2rem" color={color.grayWhite} />
        ) : (
          <FlatIcon type={IconNameEnum.IcHeartOutlined} size="2rem" />
        )}
      </button>
      <div className="product-detail-fixed-bottom__button-wrapper">
        {match(productState)
          .with(PRODUCT_STATE.isNotAvailable, () => {
            return (
              <button
                type="button"
                className="product-detail-fixed-bottom__button product-detail-fixed-bottom__button--is-not-available"
                disabled
              >
                <Typo variant={TypoVariant.Body1Bold} color={color.grayWhite}>
                  구매불가
                </Typo>
              </button>
            )
          })
          .with(PRODUCT_STATE.isRealSoldOut, () => {
            return isRequestRestockState ? (
              <button
                type="button"
                className="product-detail-fixed-bottom__button product-detail-fixed-bottom__button--is-sold-out-alarmed"
                onClick={() =>
                  handleClickRestockAlarmButton({
                    isRestockAlarm: false,
                  })
                }
              >
                <Typo variant={TypoVariant.Body1Bold} color={color.grayWhite}>
                  재입고 알림 신청 해제
                </Typo>
              </button>
            ) : (
              <button
                type="button"
                className="product-detail-fixed-bottom__button product-detail-fixed-bottom__button--is-sold-out"
                onClick={() =>
                  handleClickRestockAlarmButton({
                    isRestockAlarm: true,
                  })
                }
              >
                <Typo variant={TypoVariant.Body1Bold} color={color.grayWhite}>
                  재입고 알림 받기
                </Typo>
              </button>
            )
          })
          .otherwise(() => {
            return (
              <button
                type="button"
                className="product-detail-fixed-bottom__button product-detail-fixed-bottom__button--purchase"
                onClick={handleClickPurchaseButton}
              >
                <Typo variant={TypoVariant.Body1Bold} color={color.grayWhite}>
                  구매하기
                </Typo>
              </button>
            )
          })}
      </div>
      <ModalRestockProductOptionAdded useModalProps={restockProductOptionAddedProps} />
    </ProductDetailFixedBottomWrapper>
  )
}

const likeAnimation = keyframes`
   0% {
      transform: scale(1);
      opacity: 0;
    }
    50% {
      transition-timing-function: ease-in-out-cubic;
      opacity: 1;
      transform: scale(1.3);
    }
    100% {
      transform: scale(1);
    }
`

const ProductDetailFixedBottomWrapper = styled.div<{ isAnimating: boolean }>`
  display: flex;
  padding: 1.2rem 1.6rem var(--safe-bottom-extra);
  gap: 0.8rem;
  background-color: ${({
    theme: {
      color: { grayWhite },
    },
  }) => grayWhite};
  box-shadow: 0px -2px 6px 0px rgba(0, 0, 0, 0.05);
  .product-detail-fixed-bottom {
    &__like {
      outline: none;
      cursor: pointer;
      background-color: transparent;
      padding: 1.2rem;
      border-radius: 0.8rem;
      border: 1px solid
        ${({
          theme: {
            color: { gray200 },
          },
        }) => gray200};
      > i {
        ${({ isAnimating }) =>
          isAnimating &&
          css`
            animation: ${likeAnimation} 0.2s;
          `};
      }
    }
    &__button-wrapper {
      width: 100%;
    }
    &__button {
      width: 100%;
      cursor: pointer;
      border: none;
      border-radius: 0.8rem;
      padding: 1.3rem 1.6rem;
      background-color: transparent;
    }
    &__button--purchase,
    &__button--is-sold-out {
      background-color: ${({
        theme: {
          color: { blue500 },
        },
      }) => blue500};
    }
    &__button--is-not-available,
    &__button--is-sold-out-alarmed {
      background-color: ${({
        theme: {
          color: { gray200 },
        },
      }) => gray200};
    }
  }
`
